import React, { useState, useEffect, useMemo, useContext } from "react";
import { useHistory } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import {
    Paper,
    Grid,
    Typography,
    List,
    ListItemButton,
    ListItemText,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    TextField,
    InputAdornment,
    Divider,
    Box,
    IconButton
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Markdown from "markdown-to-jsx";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import { manualData } from "./manualData";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
    root: {
        height: "100%",
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.default,
    },
    sidebar: {
        height: "calc(100vh - 120px)",
        overflowY: "auto",
        backgroundColor: theme.palette.background.paper,
        borderRadius: "12px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        border: `1px solid ${theme.palette.mode === 'light' ? '#e0e0e0' : '#424242'}`,
    },
    contentArea: {
        height: "calc(100vh - 120px)",
        overflowY: "auto",
        padding: theme.spacing(4),
        backgroundColor: theme.palette.background.paper,
        borderRadius: "12px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        border: `1px solid ${theme.palette.mode === 'light' ? '#e0e0e0' : '#424242'}`,
        "& img": {
            maxWidth: "100%",
            height: "auto",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            marginTop: "16px",
            marginBottom: "24px",
            border: "1px solid rgba(0,0,0,0.1)",
        },
        "& h1": {
            fontSize: "2rem",
            fontWeight: 700,
            marginBottom: "1rem",
            color: theme.palette.primary.main,
            borderBottom: `2px solid ${theme.palette.divider}`,
            paddingBottom: "8px",
        },
        "& h2": {
            fontSize: "1.5rem",
            fontWeight: 600,
            marginTop: "2rem",
            marginBottom: "1rem",
            color: theme.palette.text.primary,
        },
        "& h3": {
            fontSize: "1.25rem",
            fontWeight: 600,
            marginTop: "1.5rem",
            marginBottom: "0.5rem",
        },
        "& ul, & ol": {
            paddingLeft: "40px",
            marginBottom: "16px",
        },
        "& li": {
            marginBottom: "8px",
            lineHeight: 1.6,
        },
        "& p": {
            lineHeight: 1.7,
            marginBottom: "16px",
            fontSize: "1rem",
            color: theme.palette.text.secondary,
        }
    },
    searchBar: {
        padding: theme.spacing(2),
        position: 'sticky',
        top: 0,
        backgroundColor: theme.palette.background.paper,
        zIndex: 10,
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    accordion: {
        boxShadow: "none",
        "&:before": {
            display: "none",
        },
        "&.Mui-expanded": {
            margin: 0,
        }
    },
    accordionSummary: {
        minHeight: 48,
        "&.Mui-expanded": {
            minHeight: 48,
        },
        "&:hover": {
            backgroundColor: theme.palette.action.hover,
        }
    },
    listItemActive: {
        backgroundColor: `${theme.palette.primary.main} !important`,
        color: "#fff !important",
        borderRadius: "8px", // Botmix Style
        margin: "4px 8px",
        "&:hover": {
            backgroundColor: `${theme.palette.primary.dark} !important`,
        },
        "& .MuiListItemText-primary": {
            fontWeight: "bold",
        }
    },
    listItem: {
        margin: "4px 8px",
        borderRadius: "8px",
        "&:hover": {
            backgroundColor: theme.palette.action.hover,
        }
    },
    navButtons: {
        display: "flex",
        justifyContent: "space-between",
        marginTop: theme.spacing(6),
        paddingTop: theme.spacing(2),
        borderTop: `1px solid ${theme.palette.divider}`,
    }
}));



const UserManual = () => {
    const classes = useStyles();
    const history = useHistory();
    const { user } = useContext(AuthContext);
    const [searchTerm, setSearchTerm] = useState("");
    const [expanded, setExpanded] = useState(manualData[0]?.id || false);
    const [selectedTopic, setSelectedTopic] = useState(manualData[0]?.subsections[0] || null);

    // Security Check
    // Security Check
    useEffect(() => {
        if (user && !user.super) {
            toast.error("Acceso denegado. Esta opción solo está disponible para Super Admins.");
            history.push("/");
        }
    }, [user, history]);



    // Scroll to top when topic changes
    useEffect(() => {
        const contentDiv = document.getElementById("manual-content");
        if (contentDiv) contentDiv.scrollTop = 0;
    }, [selectedTopic]);

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const handleTopicClick = (topic) => {
        setSelectedTopic(topic);
    };

    // Filtrado de temas
    const filteredData = useMemo(() => {
        if (!searchTerm) return manualData;
        const lowerSearch = searchTerm.toLowerCase();

        return manualData.map(section => ({
            ...section,
            subsections: section.subsections.filter(sub =>
                sub.title.toLowerCase().includes(lowerSearch) ||
                sub.content.toLowerCase().includes(lowerSearch)
            )
        })).filter(section => section.subsections.length > 0);
    }, [searchTerm]);

    // Navegación (Prev/Next)
    const flattenSubsections = useMemo(() => {
        let flat = [];
        manualData.forEach(section => {
            flat = [...flat, ...section.subsections];
        });
        return flat;
    }, []);

    const getCurrentIndex = () => flattenSubsections.findIndex(s => s.id === selectedTopic?.id);

    const handleNext = () => {
        const idx = getCurrentIndex();
        if (idx < flattenSubsections.length - 1) {
            const next = flattenSubsections[idx + 1];
            // Encontrar padre para expandir accordion
            const parent = manualData.find(s => s.subsections.find(sub => sub.id === next.id));
            if (parent) setExpanded(parent.id);
            setSelectedTopic(next);
        }
    };

    const handlePrev = () => {
        const idx = getCurrentIndex();
        if (idx > 0) {
            const prev = flattenSubsections[idx - 1];
            const parent = manualData.find(s => s.subsections.find(sub => sub.id === prev.id));
            if (parent) setExpanded(parent.id);
            setSelectedTopic(prev);
        }
    };

    // Loading State
    if (!user) {
        return <div style={{ padding: 20 }}>Cargando información de usuario...</div>;
    }

    if (!user.super) {
        return <div style={{ padding: 20 }}>Verificando permisos...</div>;
    }

    return (
        <MainContainer>
            <MainHeader>
                <Grid container alignItems="center">
                    <Grid item>
                        <Title>
                            <MenuBookIcon style={{ marginRight: 10, verticalAlign: "middle" }} />
                            {i18n.t("manual.title") || "Manual de Usuario"}
                        </Title>
                    </Grid>
                </Grid>
            </MainHeader>

            <Grid container spacing={3} className={classes.root}>
                {/* Sidebar de Navegación */}
                <Grid item xs={12} md={3}>
                    <Paper elevation={0} className={classes.sidebar}>
                        <div className={classes.searchBar}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                placeholder="Buscar en el manual..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </div>

                        <List component="nav" aria-labelledby="nested-list-subheader">
                            {filteredData.map((section) => (
                                <Accordion
                                    key={section.id}
                                    expanded={expanded === section.id || !!searchTerm} // Siempre expandido si hay búsqueda
                                    onChange={handleAccordionChange(section.id)}
                                    className={classes.accordion}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        className={classes.accordionSummary}
                                    >
                                        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                                            {section.title}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails style={{ padding: 0 }}>
                                        <List component="div" disablePadding>
                                            {section.subsections.map((sub) => (
                                                <ListItemButton
                                                    key={sub.id}
                                                    className={selectedTopic?.id === sub.id ? classes.listItemActive : classes.listItem}
                                                    onClick={() => handleTopicClick(sub)}
                                                >
                                                    <ListItemText
                                                        primary={sub.title}
                                                        primaryTypographyProps={{ variant: "body2" }}
                                                    />
                                                </ListItemButton>
                                            ))}
                                        </List>
                                    </AccordionDetails>
                                </Accordion>
                            ))}

                            {filteredData.length === 0 && (
                                <Box p={3} textAlign="center">
                                    <Typography variant="body2" color="textSecondary">
                                        No se encontraron resultados para "{searchTerm}"
                                    </Typography>
                                </Box>
                            )}
                        </List>
                    </Paper>
                </Grid>

                {/* Área de Contenido */}
                <Grid item xs={12} md={9}>
                    <Paper elevation={0} className={classes.contentArea} id="manual-content">
                        {selectedTopic ? (
                            <>
                                <Markdown>{selectedTopic.content}</Markdown>

                                <div className={classes.navButtons}>
                                    <IconButton
                                        onClick={handlePrev}
                                        disabled={getCurrentIndex() === 0}
                                        size="small"
                                    >
                                        <ArrowBackIosIcon fontSize="small" /> Anterior
                                    </IconButton>

                                    <IconButton
                                        onClick={handleNext}
                                        disabled={getCurrentIndex() === flattenSubsections.length - 1}
                                        size="small"
                                    >
                                        Siguiente <ArrowForwardIosIcon fontSize="small" />
                                    </IconButton>
                                </div>
                            </>
                        ) : (
                            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" opacity={0.5}>
                                <MenuBookIcon style={{ fontSize: 60, marginBottom: 16 }} />
                                <Typography variant="h6">Seleccione un tema para comenzar la lectura</Typography>
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </MainContainer>
    );
};

export default UserManual;
